import React from 'react';
import { Link } from 'react-router-dom';



const BalletVariationResults = ({ recommendations, onRestart, aiError, answers }) => {
  // Debug: Show if we're using fallback recommendations
  const usingFallback = !recommendations || (
    recommendations.perfectMatches?.[0]?.variation === "Cupid Variation" &&
    recommendations.perfectMatches?.[1]?.variation === "Peasant Variation"
  );

  if (!recommendations) {
    return (
      <div className="quiz-results">
        <div className="results-header">
          <h1>Unable to Generate Recommendations</h1>
          <p>We're sorry, but we couldn't generate personalized recommendations at this time. Please try again.</p>
          {aiError && (
            <div style={{ 
              background: '#ffebee', 
              border: '1px solid #f44336', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginTop: '1rem',
              color: '#d32f2f'
            }}>
              <strong>Error:</strong> {aiError}
            </div>
          )}
        </div>
        <button className="btn-restart" onClick={onRestart}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="quiz-results">
      <div className="results-header">
        <h1>🎭 Your Ballet Destiny Revealed</h1>
        <p>Our AI has analyzed your artistic profile and selected variations that will unlock your unique potential.</p>
        
        {/* Debug Info */}
        {usingFallback && (
          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: '8px', 
            padding: '1rem', 
            marginTop: '1rem',
            fontSize: '0.9rem',
            color: '#856404'
          }}>
            ⚠️ <strong>Debug Info:</strong> Using smart rule-based recommendations (AI quota exceeded). 
            Check browser console for errors. {aiError && `Error: ${aiError}`}
          </div>
        )}
      </div>

      {/* AI Personality Analysis Section */}
      <div className="results-section">
        <h2>🎯 Your Artistic Profile Analysis</h2>
        <div className="variation-card" style={{ 
          borderLeftColor: '#4A90E2', 
          background: '#f8f9ff',
          textAlign: 'center'
        }}>
          <p style={{ fontSize: '1rem', color: 'var(--charcoal)', lineHeight: '1.6' }}>
            {recommendations.summary || "Plot twist! We've been secretly studying your answers like a ballet fairy godmother, and here's what we discovered about your dancing spirit... You're a unique dancer with your own special energy and artistic style that will shine in the perfect variations!"}
          </p>
          
          {/* Debug: Show what answers were processed */}
          {usingFallback && answers && (
            <details style={{ marginTop: '1rem', textAlign: 'left', fontSize: '0.8rem', color: '#666' }}>
              <summary style={{ cursor: 'pointer' }}>🔍 Debug: View Your Answers</summary>
              <pre style={{ background: '#f5f5f5', padding: '0.5rem', borderRadius: '4px', overflow: 'auto' }}>
                {JSON.stringify(answers, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>

      {/* Perfect Matches Section */}
      <div className="results-section">
        <h2>✨ Your Perfect Soul Matches</h2>
        <p style={{ textAlign: 'center', color: 'var(--soft-gray)', fontSize: '0.95rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>
          These variations understand who you are as an artist and will feel like dancing your own story.
        </p>
        {recommendations.perfectMatches?.map((match, index) => (
          <div key={index} className="variation-card">
            <h3>{match.variation}</h3>
            <div className="ballet-name">{match.ballet}</div>
            <p>{match.insight}</p>
          </div>
        ))}
      </div>

      {/* Challenge Variation Section */}
      {recommendations.challengeVariation && (
        <div className="results-section">
          <h2>🔥 Your Artistic Growth Challenge</h2>
          <p style={{ textAlign: 'center', color: 'var(--soft-gray)', fontSize: '0.95rem', marginBottom: '1.5rem', fontStyle: 'italic' }}>
            This variation will stretch you in exactly the right ways to unlock new dimensions of your artistry.
          </p>
          <div className="variation-card challenge">
            <h3>{recommendations.challengeVariation.variation}</h3>
            <div className="ballet-name">{recommendations.challengeVariation.ballet}</div>
            <p>{recommendations.challengeVariation.insight}</p>
          </div>
        </div>
      )}

      {/* Expert Consultation Section */}
      <div className="results-section">
        <h2>🌟 Need Expert Guidance?</h2>
        <div className="variation-card" style={{ borderLeftColor: 'var(--primary-gold)' }}>
          <h3>Get Personal Consultation</h3>
          <p>
            Have questions about these recommendations or want 1:1 guidance on variation selection? 
            Need help choosing the perfect piece for your skill level and goals?
          </p>
          <br />
          <p>
            <strong>Email <a href="mailto:info@prixducanada.com" style={{ color: 'var(--primary-gold)', textDecoration: 'none' }}>info@prixducanada.com</a> to consult with our ballet experts.</strong>
          </p>
          <br />
          <p style={{ fontSize: '0.95rem', color: 'var(--soft-gray)' }}>
            Our team of professional ballet coaches can provide personalized advice, technical guidance, 
            and help you make the final decision that's perfect for your competitive goals.
          </p>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link 
            to="/" 
            className="btn-restart" 
            style={{ 
              textDecoration: 'none', 
              display: 'inline-block',
              color: 'white'
            }}
          >
            ← Back to Homepage
          </Link>
          <Link 
            to="/register" 
            className="btn-restart" 
            style={{ 
              textDecoration: 'none', 
              display: 'inline-block',
              background: 'var(--primary-gold)',
              color: 'white'
            }}
          >
Register for PDC
          </Link>
          <button className="btn-restart" onClick={onRestart}>
            ✨ Take Quiz Again
          </button>
        </div>
        <div style={{ 
          marginTop: '1rem', 
          fontSize: '0.9rem', 
          color: 'var(--soft-gray)',
          fontStyle: 'italic' 
        }}>
          Each time you take this quiz, the AI may discover new aspects of your artistry
        </div>
      </div>
    </div>
  );
};

export default BalletVariationResults; 